import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { useGLTF, MeshReflectorMaterial, BakeShadows, Decal, useTexture } from '@react-three/drei'
import { EffectComposer, Bloom, DepthOfField, ToneMapping } from '@react-three/postprocessing'
import { easing } from 'maath'
import { useState, useEffect, useRef } from 'react'
import * as THREE from 'three'
import useCameraStore from './store'
// import { Instances, Computers } from './Computers.jsx'
import { TextScreens } from './TextScreens.jsx'
import { InteractiveCoffeeShop } from './InteractiveCoffeeShop.jsx'
import { Model as CoffeeCupModel } from './models/CoffeeCup.jsx'
import { Model as CoffeeBarrelModel } from './models/CoffeeBarrel.jsx'
import { Model as PepeModel } from './models/Pepe.jsx'
// import { Model as RockyGroundModel } from './models/RockyGround.jsx'
import { ContactSign } from './ContactSign.jsx'
import { Model as ContactBoothModel } from './models/ContactBooth.jsx'
import { InteractiveContactBoothOptimized } from './InteractiveContactBoothOptimized.jsx'
import { AnimatedPalmLow, AnimatedPalmWind1, AnimatedPalmWind2, AnimatedPalm3 } from './AnimatedPalms.jsx'
import { DynamicDOF } from './DynamicDOF.jsx'
import { DebugStateViewer } from './DebugStateViewer.jsx'
import { Model as GrassModel } from './models/plants/Grass.jsx'
import { Model as BushModel } from './models/plants/Bush.jsx'
import { Model as PalmLowModel } from './models/plants/PalmLow.jsx'
import { StaticBrewingScreen } from './StaticBrewingScreen.jsx'

// Configure Draco loader for compressed models
useGLTF.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')

export default function App({ onProjectsClick }) {
  const [isMobile, setIsMobile] = useState(false)
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || window.innerHeight <= 600)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  return (
    <Canvas
      shadows
      dpr={[1, 1.5]}
      camera={{ position: [-1.5, 1, 5.5], fov: 45, near: 1, far: 20 }}
      gl={{
        preserveDrawingBuffer: true,
        antialias: true,
        alpha: false,
        powerPreference: "high-performance"
      }}>
      {/* Lights */}
      <color attach="background" args={['black']} />
      <hemisphereLight intensity={0.15} groundColor="black" />
      <spotLight decay={0} position={[10, 20, 10]} angle={0.12} penumbra={1} intensity={1} castShadow shadow-mapSize={1024} />
      {/* Main scene */}
      <group position={[-0, -1, 0]}>
        {/* Text screens with Gohu font */}
        <TextScreens onProjectsClick={onProjectsClick} />
        {/* Static brewing screen - no interaction, just animation loop */}
        <StaticBrewingScreen
          position={[-2.5, 2, -1.5]}
          rotation={[0, Math.PI / 30, 0]}
          scale={0.8}
        />
        {/* Plane reflections + distance blur */}
        <mesh
          receiveShadow
          rotation={[-Math.PI / 2, 0, 0]}
          onClick={(e) => {
            e.stopPropagation()
            // Zoom out when clicking on ground
            const state = useCameraStore.getState()
            if (state.isElementFocused) {
              state.handleElementClick('default')
            }
          }}
        >
          <planeGeometry args={[50, 50]} />
          <MeshReflectorMaterial
            blur={[300, 30]}
            resolution={2048}
            mixBlur={1}
            mixStrength={180}
            roughness={1}
            depthScale={1.2}
            minDepthThreshold={0.4}
            maxDepthThreshold={1.4}
            color="#202020"
            metalness={0.8}
          />
        </mesh>
        {/* Coffee shop scene - using optimized components */}
        <InteractiveCoffeeShop
          scale={1}
          position={[0, 0, 0]}
          rotation={[0, Math.PI * 0.5, 0]}
        />
        {/* <CoffeeCupModel scale={0.02} position={[-1, -0.4, 0.5]} rotation={[0, Math.PI * 0.25, 0]} /> */}
        <CoffeeBarrelModel scale={0.4} position={[isMobile ? -1.8 : -2, 0, 1.5]} />
        <PepeModel scale={1.5} position={[-0.5, 1.3, 0]} rotation={[0, Math.PI * -0.3, 0]} />
        {/* Contact Sign */}
        <ContactSign position={[1.8, 0.78, 1.55]} rotation={[0, -Math.PI / 6, 0]} scale={0.16} />
        {/* Contact Booth - Interactive 3D Model (rendered last for click priority) */}
        <InteractiveContactBoothOptimized position={[1.8, 0.8, 1.3]} rotation={[0, -Math.PI / 2, 0]} scale={1.5} />

        {/* Animated Palm Trees with Wind Effect */}
        {/* Palm Low - static, no animation */}
        <PalmLowModel
          scale={0.5}
          position={[
            -3.9,    // x: left side
            0,       // y: ground level
            -3.5     // z: behind
          ]}
        />

        {/* Bush next to Palm Low to cover base */}
        <BushModel
          scale={0.7}
          position={[
            -3.7,   // x: slightly offset from palm
            -0.13,       // y: ground level
            0.1     // z: near palm base
          ]}
          rotation={[0, Math.PI * 0.6, 0]}
        />

        {/* Palm Wind Mid - on left in front of palm_low */}
        <AnimatedPalmWind1
          scale={0.8}
          position={[
            -3.99,    // x: left side, slightly right of palm_low
            0,       // y: ground level
            -.8     // z: in front of palm_low
          ]}
        />

        {/* Grass next to Palm Wind 1 to cover base */}
        <GrassModel
          scale={0.6}
          position={[
            -3.85,   // x: slightly offset from palm
            0,       // y: ground level
            -0.9     // z: near palm base
          ]}
          rotation={[0, Math.PI * 0.3, 0]}
        />

        {/* Second PalmWind instance - rotated for variety */}
        <AnimatedPalmWind2
          scale={0.8}
          position={[
            -4.8,   // x: left side, slightly right of palm_low
            .2,       // y: ground level
            1.2     // z: in front of palm_low
          ]}
          rotation={[0, Math.PI * 0.4, 0]}  // Rotated 72 degrees for variety
        />

        {/* Bush next to Palm Wind 2 to cover base */}
        <BushModel
          scale={0.4}
          position={[
            -4.15,   // x: slightly offset from palm
            -0.05,     // y: match palm height
            0.1      // z: near palm base
          ]}
          rotation={[0, Math.PI * -0.2, 0]}
        />

        {/* Palm 3 - on right next to telephone booth */}
        <AnimatedPalm3
          scale={0.8}
          position={[
            3.3,     // x: right side near booth
            0,       // y: ground level
            .5      // z: slightly forward
          ]}
        />

        {/* Coffee+Coded logo as plane */}
        {/* <CoffeeLogo 
          position={[2.1, 1, 1]} 
          rotation={[0, Math.PI , 0]}
          scale={1.2}
        /> */}
        {/* <RockyGround scale={0.3} position={[0, 0.1, 0]} /> */}
        <pointLight distance={3} intensity={2} position={[0, 2, 0]} color="#FFA500" />
      </group>
      {/* Postprocessing */}
      <EffectComposer disableNormalPass>
        <Bloom luminanceThreshold={0.2} mipmapBlur luminanceSmoothing={0.0} intensity={3} />
        {!isMobile && <DynamicDOF />}
      </EffectComposer>
      {/* Camera movements */}
      <CameraRig />
      {/* Small helper that freezes the shadows for better performance */}
      <BakeShadows />
    </Canvas>
  )
}

// Old components removed - now using optimized versions from ./models/

function CoffeeLogo(props) {
  const logoTexture = useTexture('/Coffee+Coded.png')
  logoTexture.anisotropy = 16
  
  return (
    <mesh {...props}>
      <planeGeometry args={[1, 0.5]} />
      <meshBasicMaterial 
        map={logoTexture} 
        transparent={true}
        side={2} // Double-sided
      />
    </mesh>
  )
}



function CameraRig() {
  const {
    zoomLevel,
    targetZoomLevel,
    updateZoomLevel,
    zoomByDelta,
    panOffset,
    targetPanOffset,
    updatePanOffset,
    setTargetPan,
    setTargetZoom,
    isElementFocused
  } = useCameraStore()
  const { camera, gl } = useThree()
  const wheelTimeoutRef = useRef()
  const zoomRef = useRef({ value: zoomLevel })
  const panRef = useRef({ value: panOffset })

  // Detect mobile portrait mode
  const isMobilePortrait = window.innerWidth < 768 && window.innerHeight > window.innerWidth
  const maxZoomLimit = isMobilePortrait ? 1.2 : 3.0  // Lower max zoom on portrait
  const minZoomLimit = isMobilePortrait ? -0.5 : -2   // Less zoom out on portrait

  // Handle mouse wheel and touch gestures for zoom
  useEffect(() => {
    // Mouse wheel handler
    const handleWheel = (e) => {
      e.preventDefault()
      // Normalize wheel delta
      const delta = -e.deltaY / 100
      zoomByDelta(delta)

      // Optional: Clear focus when manually zooming
      if (isElementFocused) {
        useCameraStore.setState({ isElementFocused: false, lastClickedElement: null, targetPanOffset: 0 })
      }
    }

    // Touch gesture handlers
    let touchStartDist = 0
    let touchStartZoom = 0

    const getTouchDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    }

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        touchStartDist = getTouchDistance(e.touches)
        touchStartZoom = targetZoomLevel
      }
    }

    const handleTouchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        const currentDist = getTouchDistance(e.touches)
        const scale = currentDist / touchStartDist
        const zoomDelta = (scale - 1) * 3 // Adjust sensitivity

        useCameraStore.setState({
          targetZoomLevel: Math.max(minZoomLimit, Math.min(maxZoomLimit, touchStartZoom + zoomDelta)),
          isElementFocused: false,
          lastClickedElement: null,
          targetPanOffset: 0
        })
      }
    }

    // Add event listeners
    gl.domElement.addEventListener('wheel', handleWheel, { passive: false })
    gl.domElement.addEventListener('touchstart', handleTouchStart, { passive: false })
    gl.domElement.addEventListener('touchmove', handleTouchMove, { passive: false })

    return () => {
      gl.domElement.removeEventListener('wheel', handleWheel)
      gl.domElement.removeEventListener('touchstart', handleTouchStart)
      gl.domElement.removeEventListener('touchmove', handleTouchMove)
      if (wheelTimeoutRef.current) clearTimeout(wheelTimeoutRef.current)
    }
  }, [gl, zoomByDelta, targetZoomLevel, isElementFocused])

  useFrame((state, delta) => {
    // Update ref values
    zoomRef.current.value = zoomLevel
    panRef.current.value = panOffset

    // Smooth zoom transition
    easing.damp(zoomRef.current, 'value', targetZoomLevel, 0.25, delta)

    // Smooth pan transition
    easing.damp(panRef.current, 'value', targetPanOffset, 0.25, delta)

    // Update store if values changed
    if (Math.abs(zoomRef.current.value - zoomLevel) > 0.001) {
      updateZoomLevel(zoomRef.current.value)
    }
    if (Math.abs(panRef.current.value - panOffset) > 0.001) {
      updatePanOffset(panRef.current.value)
    }

    if (isElementFocused) {
      // FPS-style movement: move camera forward along its look direction
      // Start from default position
      const defaultX = -1.5
      const defaultY = 1
      const defaultZ = 5.5

      // Calculate direction vector from camera to look-at point (0,0,0)
      const lookDirection = new THREE.Vector3(0, 0, 0)
        .sub(new THREE.Vector3(defaultX, defaultY, defaultZ))
        .normalize()

      // Move camera forward along this direction based on zoom level
      const forwardDistance = zoomRef.current.value * 1.7
      const targetX = defaultX + lookDirection.x * forwardDistance + panRef.current.value * 0.5
      const targetY = defaultY + lookDirection.y * forwardDistance * 0.8 // Less vertical movement
      const targetZ = defaultZ + lookDirection.z * forwardDistance

      easing.damp3(
        state.camera.position,
        [targetX, targetY, targetZ],
        0.5,
        delta
      )
    } else {
      // Normal mouse control when not focused
      const baseX = -1 + (state.pointer.x * state.viewport.width) / 3
      const baseY = (1 + state.pointer.y) / 2
      const baseZ = 5.5

      easing.damp3(
        state.camera.position,
        [baseX, baseY, baseZ],
        0.5,
        delta
      )
    }

    state.camera.lookAt(0, 0, 0)
  })
}
